export interface UploadResult {
    success: boolean;
    outcome?: 'Accept' | 'Reject' | 'Warning';
    id?: string;
}

export interface User {
    id: string;
    email: string;
    name: string;
}
export interface UserDetails {
    id: string;
    first_name: string;
    last_name: string;
    email_address: string;
    avatar_url: string;
}
export enum Category {
    FOOD = 'Food',
    WINE = 'Wine',
    BEVERAGE = 'Beverage',
}

export interface PackCopy {
    id: string;
    org_id: string;
    project_id: string;
    creation: Creation;
    deleted: Deleted;
    name: string;
    notes?: string;
    metadata: Record<string, never>;
    file_format?: FileFormat;
}

export interface LabelStatus {
    is_active: boolean;
}

export enum ApprovalStatus {
    // AI_APPROVED = 'AI Approved',
    APPROVED = 'Approved',
    REFERRED = 'Referred',
    REJECTED = 'Rejected',
}

export interface LabelCheckResult {
    id: string;
    user_id: string;
    org_id: string;
    spec_id: string;
    status: LabelStatus;
    user_input: UserInput;
    run_info: RunInfo;
    approval_status: ApprovalStatus;
}

export interface SpecToken {
    text: string;
}

export interface BoundingRect {
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
}

export interface LabelToken {
    text: string;
    bounding_rect: BoundingRect;
}

export enum SpecType {
    SPEC_STRING = 'SPEC_STRING',
    SPEC_STRING_LIST = 'SPEC_STRING_LIST',
    SPEC_TABLE = 'SPEC_TABLE',
    SPEC_SYMBOL = 'SPEC_SYMBOL',
}

export enum SpecCheckOutcome {
    PASS = 'PASS',
    WARNING = 'WARNING',
    FAIL = 'FAIL',
}

export enum TokenMatchType {
    FULL_MATCH = 'FULL_MATCH',
    NORMALISED_MATCH = 'NORMALISED_MATCH',
    PARTIAL_MATCH = 'PARTIAL_MATCH',
    NO_MATCH = 'NO_MATCH',
    IRRELEVANT_TOKEN = 'IRRELEVANT_TOKEN',
}

export enum SequenceMatchType {
    FULL_MATCH = 'FULL_MATCH',
    PARTIAL_MATCH = 'PARTIAL_MATCH',
    NO_MATCH = 'NO_MATCH',
}

export interface CheckedToken {
    pack_copy_token: SpecToken | null;
    label_token: LabelToken | null;
    token_match_type: TokenMatchType;
    match_ratio: number;
}

export interface MatchSequence {
    checked_tokens: CheckedToken[];
    sequence_match_ratio: number;
    sequence_match_type: SequenceMatchType;
}

export interface SpecTableRowChecked {
    header: boolean;
    name: string;
    values: (MatchSequence | null)[];
}

export interface SpecTableChecked {
    col_names: string[] | null;
    rows: SpecTableRowChecked[];
}

export interface SpecTableRow {
    header: boolean;
    name: string;
    values: (string | null)[];
}

export interface SpecTableContent {
    headers: (string | null)[];
    rows: (string | null)[][];
}

export interface Summary {
    text: string | string[];
    bullets: string[] | null;
}

export interface Dimensions {
    height: number;
    width: number;
}

export interface DetectedSymbol {
    value: number;
    bounding_rect: BoundingRect;
    rotation: 0 | 90 | 180 | 270;
    dimensions: Dimensions;
}

export interface SpecSymbolResults {
    detected_symbols: DetectedSymbol[];
    symbol_id: string;
    symbol_height: SizeReq | null;
}

export interface SpecStringResults {
    match_sequence: MatchSequence;
    text_size: number | null;
}

export interface SpecStringListResults {
    match_sequences: MatchSequence[];
    text_size: number | null;
}

export interface ProjectLibraryRequestParams {
    pageNumber: number;
    numItems: string;
    filters: AppliedFilter[];
    // countries: string[];
    // industries: string[];
    search?: string;
    sortField?: string;
    sortOrder?: string;
}

export interface NutritionFopInfo {
    label: string;
    amount: string;
    statement: 'High' | 'Medium' | 'Low' | null;
    ri_percent: string;
}

export interface NutritionsFop {
    energy: NutritionFopInfo;
    fat: NutritionFopInfo;
    saturates: NutritionFopInfo;
    sugars: NutritionFopInfo;
    salt: NutritionFopInfo;
}

export interface NutritionBopRow {
    label: string;
    amount_per_100g: string;
    amount_per_portion: string;
    ri_percent: string;
}

export interface NutritionsBop {
    headers: NutritionBopRow;
    energy: NutritionBopRow;
    fat: NutritionBopRow;
    saturates: NutritionBopRow;
    carbohydrates: NutritionBopRow;
    sugars: NutritionBopRow;
    fibre: NutritionBopRow;
    protein: NutritionBopRow;
    salt: NutritionBopRow;
}

export interface FoodSpecs {
    fop_nutritions: NutritionsFop;
    bop_nutritions: NutritionsBop;
    legal_name: string;
    cooking_instructions: string[];
    serving_number: string | null;
    reference_intake_energy: string | null;
    declared_quantity: string | null;
    ingredients: string | null;
}

export interface WineSpecs {
    legal_name: string; // The legal name and description of the wine.
    storage_instructions: string; // Instructions on how to store the wine.
    energy_information: string[]; // Caloric and energy content information per servings.
    serving_number: string; // Information on how many servings are in the bottle.
    declared_quantity: string; // The total volume of the wine bottle.
    abv: string; // Alcohol by volume expressed as a percentage.
    origin_statement: string; // The statement of origin for the wine.
}

export interface SizeReq {
    min: number | null;
    max: number | null;
}

export interface SpecStringContent {
    text: string;
    text_size: SizeReq | null;
}

export interface SpecStringListContent {
    text: string[];
    text_size: SizeReq | null;
}

export interface SpecSymbolContent {
    symbol_id: string;
    symbol_height: SizeReq | null;
}

export interface SpecDetail {
    name: string;
    display_name: string;
    description: string | null;
    spec_type: SpecType;
    language: string | null;
    market: string | null;
    content:
        | SpecStringContent
        | SpecStringListContent
        | SpecTableContent
        | SpecSymbolContent;
}
export interface SpecDetailStringList extends SpecDetail {
    content: SpecStringListContent;
    spec_type: SpecType.SPEC_STRING_LIST;
}
export interface SpecDetailTable extends SpecDetail {
    content: SpecTableContent;
    spec_type: SpecType.SPEC_TABLE;
}

export interface SpecDetailSymbol extends SpecDetail {
    content: SpecSymbolContent;
    spec_type: SpecType.SPEC_SYMBOL;
}
export interface SpecDetailString extends SpecDetail {
    spec_type: SpecType.SPEC_STRING;
    content: SpecStringContent;
}

export interface PackCopyWithLanguages extends PackCopy {
    languages?: string[];
}

export interface PackCopyDetailed extends PackCopy {
    specs: SpecDetail[];
}

export interface AnalyticsDataResponse {
    filter_count_dict: Record<string, Record<string, number>>;
    user_outcome_dict: {
        user: Record<string, number>;
        outcome: Record<string, number>;
    };
    rule_failure_dict: Record<
        string,
        {
            count: number;
            rule_description: string;
            short_rule_description: string;
            rule_type: 'Fail' | 'Warning';
        }
    >;
}

export interface UserInput {
    market: string;
    asset_name: string;
}

export interface RunInfo {
    timestamp: number;
    run_duration: number;
    run_type: 'live' | 'local';
}

export interface UserInfo {
    username: string;
    email: string;
}

export interface ImageData {
    text: ExtractedText;
}

export interface ExtractedText {
    native_text: string;
    en_text: string;
    language_code: string;
}

export interface SortOption {
    label: string;
    variable: 'id' | 'industry' | 'user' | 'country' | 'date';
    sortOrder: 'ascending' | 'descending';
}

export interface DeleteData {
    item_id: string;
    user: string;
}

export type State =
    | 'pending'
    | 'uploading'
    | 'processing'
    | 'successful'
    | 'failed';
export type UploadType = 'spec' | 'label';

export enum AssetType {
    LABEL = 'label',
    PACK_COPY = 'pack-copy',
}

export interface UploadState {
    id: string;
    state: State;
    upload_type: UploadType;
    asset_type: AssetType;
    file_format: FileFormat;
    user_id: string;
    org_id: string;
    project_id: string;
    asset_name: string;
    upload_time: number;
    display: boolean;
}

export interface OngoingUploadState extends UploadState {
    state: 'pending' | 'uploading' | 'processing';
}

export interface FailedUploadState extends UploadState {
    state: 'failed';
    error_message: string;
}

export interface SuccessfulUploadState extends UploadState {
    state: 'successful';
    outcome: string;
}

export enum FileFormat {
    PDF = 'pdf',
    JPEG = 'jpeg',
    JPG = 'jpg',
    PNG = 'png',
    DOC = 'doc',
    DOCX = 'docx',
    XLS = 'xls',
    XLSX = 'xlsx',
    CSV = 'csv',
}

export interface FileConfig {
    file_format: FileFormat;
}

export interface ImageFileConfig extends FileConfig {
    file_format: FileFormat.JPEG | FileFormat.JPG | FileFormat.PNG;
}

export interface PDFFileConfig extends FileConfig {
    file_format: FileFormat.PDF;
    page: number;
}

export interface PackCopyFileConfig extends FileConfig {
    file_format:
        | FileFormat.DOC
        | FileFormat.DOCX
        | FileFormat.XLS
        | FileFormat.XLSX
        | FileFormat.CSV
        | FileFormat.PDF;
}

export interface LabelProperties {
    version_name: string;
    notes?: string;
    project_id: string;
    pack_copy_id: string;
}

export interface PackCopyProperties {
    version_name: string;
    notes?: string;
    project_id: string;
}

export interface LabelReviewData {
    file_config: ImageFileConfig | PDFFileConfig;
    properties: LabelProperties;
}

export interface PackCopyIngestData {
    file_config: PackCopyFileConfig;
    properties: PackCopyProperties;
}

export interface FilterConfig {
    name: FilterType;
    display_name: string;
}

export interface BoundingRect {
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
}

export interface AssetStatus {
    is_active: boolean;
}

export interface RunInfo {
    timestamp: number;
    run_duration: number;
    run_type: 'live' | 'local';
}

export enum Market {
    BELUX = 'BeLux',
    FRANCE = 'France',
    NETHERLANDS = 'Netherlands',
}

export enum AlcoholStrength {
    ABV_0_0 = '0.0% ABV',
    ABV_OVER_0_5 = '>0.5% ABV',
}

export enum YesNo {
    YES = 'Yes',
    NO = 'No',
}

export enum Brand {
    JUPILER = 'Jupiler',
    VICTORIA = 'Victoria',
    TRIPEL_KARMELIET = 'Tripel Karmeliet',
    STELLA_ARTOIS = 'Stella Artois',
    LEFFE = 'Leffe',
    BUD = 'Bud',
    HERTOG_JAN = 'Hertog Jan',
    CORONA = 'Corona',
    OTHER = 'Other',
}

export interface BaseAssetProperties {
    asset_name: string;
    market: Market;
    brand: Brand;
}

export interface ProjectProperties {
    name: string;
    category: Category;
}

export interface DutchAssetProperties extends BaseAssetProperties {
    market: Market.NETHERLANDS;
}

export interface BelgiumAssetProperties extends BaseAssetProperties {
    market: Market.BELUX;
    on_public_roads: YesNo;
}

export interface FrenchAssetProperties extends BaseAssetProperties {
    market: Market.FRANCE;
}

// Base ReviewStage interface
export interface ReviewStage {
    id: string;
    user_id: string;
    status: ApprovalStatus.APPROVED | ApprovalStatus.REJECTED | 'Pending';
    timestamp: number;
}

// PendingReviewStage interface
export interface PendingReviewStage extends ReviewStage {
    status: 'Pending';
}

// ReviewedStage interface
export interface CompletedReviewStage extends ReviewStage {
    status: ApprovalStatus.APPROVED | ApprovalStatus.REJECTED;
    comment: string | null;
}

export interface Label {
    id: string;
    org_id: string;
    project_id: string;
    creation: Creation; // Reusing the Creation type from spec-types
    deleted: Deleted; // Reusing the Deleted type from spec-types
    name: string;
    notes?: string;
    metadata: Record<string, never>;
    approval_status: ApprovalStatus;
    reviewed_stages: (PendingReviewStage | CompletedReviewStage)[];
    file_format: FileFormat; // Reusing the FileFormat enum from spec-types
}

export interface LabelDetailed extends Label {
    approval_status_history: ApprovalStatusChange[];
    comments: Comment[];
    rule_results: RuleResult[];
}

export enum SpecRuleType {
    SPEC_STRING = 'SPEC_STRING',
    SPEC_STRING_LIST = 'SPEC_STRING_LIST',
    SPEC_TABLE = 'SPEC_TABLE',
    SPEC_SYMBOL = 'SPEC_SYMBOL',
}

export enum BasicRuleType {
    STANDARD = 'STANDARD',
}

export type RuleType = SpecRuleType | BasicRuleType;

export interface Rule {
    name: string;
    display_name: string;
    description: string;
    type: RuleType;
}

export enum Outcome {
    PASS = 'Pass',
    WARNING = 'Warning',
    FAIL = 'Fail',
}

export enum OutcomeChangeType {
    INITIAL = 'initial',
    USER_FEEDBACK = 'user feedback',
}

export interface OutcomeChange {
    change_type: OutcomeChangeType;
    reason: string;
    user_id: string;
}

export interface OutcomeChangeInitial extends OutcomeChange {
    chage_type: OutcomeChangeType.INITIAL;
}

export interface OutcomeChangeUserFeedback extends OutcomeChange {
    chage_type: OutcomeChangeType.USER_FEEDBACK;
    timestamp: number;
}

export interface VisualEvidence {
    notes: string[] | null;
    bounding_rects: BoundingRect[];
    outcome: Outcome;
}
export interface UserFeedback {
    reason: string;
    user_id: string;
    timestamp: number;
}

export interface SpecTableResults {
    headers: MatchSequence[];
    rows: MatchSequence[][];
    text_size: number | null;
}

export interface RuleResult extends Rule {
    outcome: Outcome;
    summary: Summary;
    copy_summary?: string;
    evidence_items: VisualEvidence[];
    user_feedback_history: UserFeedback[];
    output_data?:
        | SpecStringResults
        | SpecStringListResults
        | SpecTableResults
        | SpecSymbolResults;
}
export enum ApprovalStatusChangeType {
    INITIAL = 'initial',
    USER_CHANGE = 'user change',
}

export enum ActivityType {
    UPLOAD = 'upload',
    COMMENT = 'comment',
    STATUS_CHANGE = 'status change',
    USER_FEEDBACK = 'user feedback',
}

export interface ApprovalStatusChange {
    new_status: ApprovalStatus;
    change_type: ApprovalStatusChangeType;
    reason?: string;
    user_id: string;
    timestamp: number;
}

export interface Comment {
    id: string;
    content: string;
    user_id: string;
    timestamp: number;
}

export interface UploadActivityContent {
    approval_status: ApprovalStatus;
    asset_name: string;
}

export interface CommentActivityContent {
    content: string;
}

export interface StatusChangeActivityContent {
    old_status: ApprovalStatus;
    new_status: ApprovalStatus;
    reason: string | null;
}

export interface UserFeedbackActivityContent {
    rule_id: string;
    rule_name: string;
    old_outcome: Outcome;
    reason: string;
}

export interface Activity {
    activity_type: ActivityType;
    user_details: UserDetails | null;
    timestamp: number;
    content:
        | UploadActivityContent
        | CommentActivityContent
        | StatusChangeActivityContent
        | UserFeedbackActivityContent;
}

export interface UploadActivity extends Activity {
    activity_type: ActivityType.UPLOAD;
    content: UploadActivityContent;
}

export interface CommentActivity extends Activity {
    activity_type: ActivityType.COMMENT;
    content: CommentActivityContent;
}

export interface StatusChangeActivity extends Activity {
    activity_type: ActivityType.STATUS_CHANGE;
    content: StatusChangeActivityContent;
}

export interface UserFeedbackActivity extends Activity {
    activity_type: ActivityType.USER_FEEDBACK;
    content: UserFeedbackActivityContent;
}

export interface ProjectLibraryResponse {
    projects: Project[];
    user_details_mapping: { [user_id: string]: UserDetails | null };
    total_pages: number;
    prev_page: number | null;
    next_page: number | null;
    outgoing_filters: OutgoingFilter[];
}

export enum FilterType {
    USER = 'user',
    DATE = 'date',
    APPROVAL = 'approval',
    BRAND = 'brand',
}

export interface FilterValue {
    name: string;
    display_name: string;
}

export interface FilterOption {
    value: FilterValue;
    count: number;
}

export interface Filter {
    type: FilterType;
}

export interface OutgoingFilter extends Filter {
    options: FilterOption[];
}

export interface AppliedFilter extends Filter {
    selected_values: FilterValue[];
}

export interface ProjectMetadata {
    category: Category;
}

export interface ProjectAccess {
    admins: string[]; // User IDs of project admins
}

export interface Stage {
    id: string;
    name: string;
    order: number;
    reviewer: string; // User IDs of possible reviewers
}

export interface ProjectWorkflow {
    stages: Stage[];
}

export interface Creation {
    timestamp: number;
    user_id: string;
}

export interface Deleted {
    is_deleted: boolean;
    user_id: string | null;
    timestamp: number | null;
}

export interface Project {
    id: string;
    org_id: string;
    creation: Creation;
    deleted: Deleted;
    name: string;
    metadata: ProjectMetadata;
    workflow: ProjectWorkflow;
    access: ProjectAccess;
}

export interface ProjectsResponse {
    projects: Project[];
    user_details_mapping: Record<string, UserDetails | null>;
    outgoing_filters: OutgoingFilter[];
    total_pages?: number;
    prev_page?: number;
    next_page?: number;
}

export interface ProjectDetailResponse {
    project: Project;
    user_details_mapping: Record<string, UserDetails | null>; // UserDetails from label-types
    labels: Label[]; // Label from label-types
    label_thumbnail_urls: Record<string, string>;
    pack_copies: PackCopyWithLanguages[];
}

export interface LabelResponse {
    label_detailed: LabelDetailed;
    image_url: string;
    activities: (
        | StatusChangeActivity
        | CommentActivity
        | UploadActivity
        | UserFeedbackActivity
    )[];
}

export interface PackCopyResponse {
    pack_copy_detailed: PackCopyDetailed;
}

export interface RuleResultSpecSymbol extends RuleResult {
    check_type: SpecRuleType.SPEC_SYMBOL;
    output_data: SpecSymbolResults;
}

// New pack copy ingest types
export enum PackCopyIngestMode {
    AUTOMATIC = 'automatic',
    MANUAL = 'manual',
}

export interface PackCopyPostDataAutomatic {
    file_config: PackCopyFileConfig;
    pack_copy_id: string;
}

export interface PackCopyPostRequest {
    mode: PackCopyIngestMode;
    properties: PackCopyProperties;
    data: PackCopyPostDataAutomatic | SpecDetail[];
}

export interface PackCopyPostRequestAutomatic extends PackCopyPostRequest {
    mode: PackCopyIngestMode.AUTOMATIC;
    data: PackCopyPostDataAutomatic;
}

export interface PackCopyPostRequestManual extends PackCopyPostRequest {
    mode: PackCopyIngestMode.MANUAL;
    data: SpecDetail[];
}
